// @givmo/design-system — Disclosure.
//
// A quiet text toggle that shows or hides a section, on the native `<details>` / `<summary>` pair
// (Charity Portal design 20 §20 C15): the browser gives it its keyboard behavior (Enter and Space
// on the summary) and its screen-reader state (expanded or collapsed), and it opens and closes
// from the server-rendered markup before any script has run. The label, then a chevron that points
// right while closed and turns down once open; `openLabel` swaps the words with the state ("Show
// more" / "Show less"). Both the swap and the turn are CSS keyed on the element's own `[open]`, so
// neither waits for hydration, and nothing here sets a `style` attribute (the donate host serves a
// nonce-only `style-src`).
//
// What opening reveals follows the summary, as `<details>` requires. A surface that grows content
// ABOVE the toggle (an excerpt that becomes its full text, as the donate page's charity hero does)
// marks a common ancestor `DISCLOSURE_HOST` and keys that content on the element's state with
// `WHILE_DISCLOSURE_CLOSED` / `WHILE_DISCLOSURE_OPEN` (CSS `:has(details[open])`, no script).
//
// The element owns its open state: `defaultOpen` sets the first paint, and `onOpenChange` hears
// each change (the element's `toggle` event), so a caller never holds a copy that can drift from
// what the page shows.
import type { ReactNode, SyntheticEvent } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '../lib/utils';
import { FOCUS_RING } from '../lib/focus';
import { testIdProp, type TestIdProps } from '../lib/testid';

export interface DisclosureProps extends TestIdProps {
  /** The summary's words while the section is closed. */
  label: ReactNode;
  /** The words while it is open; the closed words when omitted. */
  openLabel?: ReactNode;
  /** What opening it shows, after the summary. */
  children?: ReactNode;
  /** Open on first paint. Closed by default. */
  defaultOpen?: boolean;
  /** Hears each change a person makes, with the new state. */
  onOpenChange?: (open: boolean) => void;
  /** The `<details>` element's classes (its width, its place in the surface). */
  className?: string;
  /** The summary's classes, merged over its recipe (alignment, spacing). */
  summaryClassName?: string;
}

/** On a common ancestor of a disclosure and the content above it that follows its state. */
export const DISCLOSURE_HOST = 'group/disclosure-host';
/** Shown while the host's disclosure is closed (an excerpt), gone while it is open. */
export const WHILE_DISCLOSURE_CLOSED = 'group-has-[details[open]]/disclosure-host:hidden';
/** Gone while the host's disclosure is closed, shown while it is open (the full text). */
export const WHILE_DISCLOSURE_OPEN = 'hidden group-has-[details[open]]/disclosure-host:block';

/**
 * The summary's recipe: brand-blue semibold text with a 16px chevron, a 37px-tall target (8px above
 * and below the 14px line), the house focus ring. `list-none` and the WebKit marker rule drop the
 * native triangle, which the chevron replaces.
 */
const SUMMARY =
  'flex w-fit cursor-pointer list-none items-center gap-2 rounded-all4 px-4 py-8 text-s14 font-w600 tracking-normal text-blue outline-none select-none [&::-webkit-details-marker]:hidden';

export function Disclosure({
  label,
  openLabel,
  children,
  defaultOpen = false,
  onOpenChange,
  className,
  summaryClassName,
  testId,
}: DisclosureProps) {
  const onToggle = onOpenChange
    ? (event: SyntheticEvent<HTMLDetailsElement>) => onOpenChange(event.currentTarget.open)
    : undefined;
  return (
    <details
      className={cn('group/disclosure', className)}
      {...(defaultOpen ? { open: true } : {})}
      onToggle={onToggle}
    >
      <summary className={cn(SUMMARY, FOCUS_RING, summaryClassName)} {...testIdProp(testId)}>
        {openLabel === undefined ? (
          label
        ) : (
          <>
            <span className="group-open/disclosure:hidden">{label}</span>
            <span className="hidden group-open/disclosure:inline">{openLabel}</span>
          </>
        )}
        {/* Tailwind 4's `rotate-90` writes the `rotate` property, so that is what transitions. */}
        <ChevronRight
          aria-hidden
          className="size-16 shrink-0 transition-[rotate] duration-200 group-open/disclosure:rotate-90 motion-reduce:transition-none"
        />
      </summary>
      {children}
    </details>
  );
}
